import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { AuthorizedRoute } from '@/components/AuthorizedRoute/AuthorizedRoute'
import { DataTag } from '@/components/DataTag/DataTag'
import SkeletonTable from '@/components/SkeletonTable'
import TableFooter from '@/components/TableFooter/TableFooter'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { dynamicTemplateComparison } from '@/features/Templates/components/TemplateComparison'
import { useGetTemplates } from '@/generated/http-clients/v3ApiComponents'
import { FIRST_PAGE } from '@/helpers/constants'
import { timeAgo } from '@/helpers/timeFormatter'
import { templatesRootRoute } from '@/router/rootRoutes'
import { NBSP } from '@/utils'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Flex,
  HStack,
  Icon,
  Table,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link, createRoute, useNavigate } from '@tanstack/react-router'
import { Archive, ArchiveX, Plus } from 'lucide-react'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './templateTable.module.css'

export const templateTableRoute = createRoute({
  getParentRoute: () => templatesRootRoute,
  path: '/',
  component: () => (
    <AuthorizedRoute>
      <TemplateTable />
    </AuthorizedRoute>
  ),
})

export function TemplateTable() {
  const nav = useNavigate()
  const { t } = useTranslation(undefined, { useSuspense: false })
  const [page, setPage] = useState(1)
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [includeArchived, setIncludeArchived] = useState(false)

  const templates = useGetTemplates(
    {
      queryParams: { page, order: 'desc', includeArchived },
    },
    {
      refetchInterval: 20000,
    },
  )

  // detect when the list of templates has been updated
  React.useEffect(() => {
    // when the templates data is updated, check whether items
    // in selectedRows still exist in the template list.
    // Remove all the ones that do not exist anymore.
    const validIds =
      templates?.data?.templates?.map((item) => item.template_id) || []
    setSelectedRows((prevSelected) =>
      prevSelected.filter((id) => validIds.includes(id)),
    )
  }, [templates?.data?.templates])

  function handleNextPage() {
    if (templates.data?.has_next_page) {
      setPage((prev) => prev + 1)
    }
  }

  function handlePreviousPage() {
    if (page > 1) {
      setPage((prev) => prev - 1)
    }
  }

  function goToFirstPage() {
    setPage(FIRST_PAGE)
  }

  function goToLastPage() {
    const newPage = templates.data?.total_pages ?? page
    setPage(newPage)
  }

  function handleCheckboxChange(id: string) {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id))
    } else if (selectedRows.length < 2) {
      setSelectedRows([...selectedRows, id])
    }
  }

  const handleCompare = () => {
    nav({
      to: dynamicTemplateComparison.to,
      params: { templateId0: selectedRows[0], templateId1: selectedRows[1] },
    })
  }

  const skeletonWidths = [32, 50, 250, 150]

  return (
    <PageLayout
      title={t('sidebar.templates')}
      action={
        <Flex justifyContent="flex-end" gap="4">
          {!templates.isLoading &&
            (templates.data?.templates || []).length > 1 && (
              <Tooltip.Root>
                <Tooltip.Trigger>
                  <Button
                    variant="outline"
                    onClick={handleCompare}
                    disabled={selectedRows.length !== 2}
                  >
                    Compare changes
                  </Button>
                </Tooltip.Trigger>
                {selectedRows.length !== 2 && (
                  <Tooltip.Positioner>
                    <Tooltip.Content>
                      Select 2 revisions to compare
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                )}
              </Tooltip.Root>
            )}
          <Link to="/templates/create">
            <Button variant="solid">
              <Icon as={Plus} />
              New template
            </Button>
          </Link>
        </Flex>
      }
    >
      <Button
        aria-label="Toggle include archived templates"
        onClick={() => setIncludeArchived(!includeArchived)}
        height={6}
        size="sm"
        pl={2}
        py={2}
        variant={includeArchived ? 'solid' : 'outline'}
        colorPalette={includeArchived ? 'blue' : 'gray'}
        borderColor={includeArchived ? 'blue.500' : 'gray.300'}
        data-testid="include-archived-filter"
      >
        <Icon as={includeArchived ? Archive : ArchiveX} />
        {includeArchived
          ? t('templates.filters.includingArchived', { ns: 'features' })
          : t('templates.filters.excludingArchived', { ns: 'features' })}
      </Button>
      <Card.Root w="100%">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader width="32px" paddingRight={0} />
              <Table.ColumnHeader>ID</Table.ColumnHeader>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Tags</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {templates.isLoading && (
              <SkeletonTable skeletonWidths={skeletonWidths} />
            )}

            {(templates.isError || !templates.data) && (
              <Table.Row>
                <Table.Cell colSpan={skeletonWidths.length}>
                  <ApiErrorState
                    apiResource={templates as unknown as UseQueryResult}
                    errorTitle="An error happened"
                    errorDescription="We failed to load the data from the server for the templates."
                  />
                </Table.Cell>
              </Table.Row>
            )}

            {templates.isSuccess &&
              templates?.data?.templates?.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan={skeletonWidths.length}>
                    <Text>{t('templates.noResults')}</Text>
                  </Table.Cell>
                </Table.Row>
              )}

            {templates.isSuccess &&
              templates?.data?.templates?.length > 0 &&
              templates?.data?.templates?.map((template) => (
                <Table.Row
                  key={template.template_id}
                  className={styles.trAlignContentTop}
                >
                  <Table.Cell
                    data-testid="checkbox"
                    pr={0}
                    className={styles.trAlignContentTopTdCheckbox}
                  >
                    <Checkbox.Root
                      checked={selectedRows.includes(template.template_id)}
                      onCheckedChange={() =>
                        handleCheckboxChange(template.template_id)
                      }
                      disabled={
                        selectedRows.length === 2 &&
                        !selectedRows.includes(template.template_id)
                      }
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                    </Checkbox.Root>
                  </Table.Cell>
                  <Table.Cell data-testid="template-id" width={50}>
                    <DataTag value={template.template_id} hasCopy={false}>
                      {template.template_id}
                    </DataTag>
                  </Table.Cell>
                  <Table.Cell data-testid="template-name">
                    <HStack gap={2}>
                      <Link
                        to="/templates/$templateId"
                        params={{ templateId: template.template_id }}
                      >
                        <Text
                          fontWeight={700}
                          whiteSpace="nowrap"
                          textOverflow="ellipsis"
                          overflow="hidden"
                          _hover={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          {template.name}
                        </Text>
                      </Link>
                      {template.archived && (
                        <Badge colorPalette="gray">
                          {t('templates.archivedBadge', { ns: 'features' })}
                        </Badge>
                      )}
                    </HStack>
                    <Text fontSize="sm" color="gray">
                      {template.created_at && (
                        <>
                          Created {timeAgo(template.created_at)} by{NBSP}
                          {template.created_by}
                        </>
                      )}
                    </Text>
                  </Table.Cell>
                  <Table.Cell data-testid="tags">
                    <HStack gap={1}>
                      {template.tags?.map((tag) => (
                        <DataTag
                          key={tag}
                          value={tag}
                          colorPalette="orange"
                          hasCopy={false}
                        >
                          {tag}
                        </DataTag>
                      ))}
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>
        </Table.Root>
      </Card.Root>

      {templates.isSuccess && (
        <TableFooter
          page={page}
          totalPages={templates.data?.total_pages ?? page}
          hasNextPage={Boolean(templates.data?.has_next_page)}
          onFirstPage={goToFirstPage}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          onLastPage={goToLastPage}
        />
      )}
    </PageLayout>
  )
}
